// The floating feedback button, part of the removable feedback widget: see
// "Removing the feedback widget" in DECISIONS.md.
//
// Renders nothing unless the signed-in role holds the FEEDBACK action, so
// App.jsx mounts it unconditionally with one line. The note records the
// route it was written on, labeled with the matching navigation tab, and
// lands in the Feedback list on the Settings page.

import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth.jsx'
import { ACTION, viewForPath } from '../../../shared/permissions.js'
import { useApiForm } from '../useApiForm.js'
import { Button, Input, Notice, Panel } from '../components/ui/index.js'
import { sendFeedback } from './feedbackApi.js'
import './feedback.css'

const MAX_SCREENSHOTS = 3

// The navigation tab this path belongs to, so an entry reads "Onsites"
// rather than a raw route.
const areaFor = (pathname) => viewForPath(pathname)?.label ?? null

export default function FeedbackButton() {
  const { can } = useAuth()
  const location = useLocation()
  const fileInput = useRef(null)
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState('')
  const [screenshots, setScreenshots] = useState([])
  const [sent, setSent] = useState(false)
  const { saving, error, submit } = useApiForm()

  if (!can(ACTION.FEEDBACK)) return null

  const area = areaFor(location.pathname)

  function addScreenshots(event) {
    const picked = [...(event.target.files ?? [])]
    setScreenshots((current) => [...current, ...picked].slice(0, MAX_SCREENSHOTS))
    event.target.value = ''
  }

  function dropScreenshot(index) {
    setScreenshots((current) => current.filter((_, i) => i !== index))
  }

  async function send() {
    const result = await submit(() =>
      sendFeedback({ comment, path: location.pathname, area, screenshots }),
    )
    if (result) {
      setComment('')
      setScreenshots([])
      setSent(true)
    }
  }

  function toggle() {
    setOpen((was) => !was)
    setSent(false)
  }

  return (
    <>
      {open ? (
        <div className="feedback-pop" role="dialog" aria-label="Send feedback">
          <Panel
            title="Feedback"
            support={`About ${area ? `the ${area} area` : 'this screen'}. Lands in the Feedback list on the Settings page.`}
          >
            {error ? <Notice tone="bad">{error}</Notice> : null}
            {sent && !error ? <Notice tone="ok">Sent. Thank you.</Notice> : null}

            <Input
              id="feedback-comment"
              label="What should we know?"
              multiline
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />

            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              className="visually-hidden"
              tabIndex={-1}
              onChange={addScreenshots}
            />

            {screenshots.length ? (
              <div className="rows">
                {screenshots.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="row">
                    <div className="row-main">
                      <div className="row-primary wraps">{file.name}</div>
                    </div>
                    <div className="row-trail">
                      <Button variant="ghost" onClick={() => dropScreenshot(index)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="btnrow">
              <Button
                onClick={() => fileInput.current?.click()}
                disabled={screenshots.length >= MAX_SCREENSHOTS}
              >
                Add screenshot
              </Button>
              <span className="spacer" />
              <Button variant="primary" onClick={send} disabled={saving || !comment.trim()}>
                {saving ? 'Sending' : 'Send feedback'}
              </Button>
            </div>
          </Panel>
        </div>
      ) : null}

      <Button variant="primary" className="feedback-fab" aria-expanded={open} onClick={toggle}>
        {open ? 'Close' : 'Feedback'}
      </Button>
    </>
  )
}
